<template>
    <div v-if="visible" class="ccn-overlay" @click.self="onCancel">
        <div class="ccn-modal" role="dialog" aria-modal="true">
            <!-- Header -->
            <div class="ccn-header">
                <h2 class="ccn-title">{{ $t('invoices.credit_note_modal.title') }}</h2>
                <button type="button" class="ccn-close" aria-label="close" @click="onCancel">
                    <X :size="20" color="#ffffff" />
                </button>
            </div>

            <!-- Gradient banner: invoice number + creditable amount. When the invoice already
                 carries avoirs, the amount shown is what REMAINS creditable (server-computed). -->
            <div class="ccn-banner">
                <div class="ccn-banner__col">
                    <span class="ccn-banner__label">{{ $t('invoices.credit_note_modal.invoice') }}</span>
                    <span class="ccn-banner__value">#{{ data.invoice_number }}</span>
                    <span v-if="hasPriorCreditNotes" class="ccn-banner__hint">
                        {{
                            $t('invoices.credit_note_modal.already_credited', {
                                amount: formatCurrency(data.already_credited),
                            })
                        }}
                    </span>
                </div>
                <div class="ccn-banner__col ccn-banner__col--right">
                    <span class="ccn-banner__label">
                        {{
                            hasPriorCreditNotes
                                ? $t('invoices.credit_note_modal.remaining_to_credit')
                                : $t('invoices.credit_note_modal.total_amount')
                        }}
                    </span>
                    <span class="ccn-banner__value">{{ formatCurrency(data.total_refund) }}</span>
                </div>
            </div>

            <!-- Motif (required) -->
            <div class="ccn-field">
                <label class="ccn-label" for="ccn-motif">
                    {{ $t('invoices.credit_note_modal.reason') }} <span class="ccn-required">*</span>
                </label>
                <div class="ccn-select-wrapper">
                    <select id="ccn-motif" v-model="motif" class="ccn-select">
                        <option value="" disabled>{{ $t('invoices.credit_note_modal.select_reason') }}</option>
                        <option v-for="key in data.reasons" :key="key" :value="key">
                            {{ $t('invoices.credit_note_modal.reasons.' + key) }}
                        </option>
                    </select>
                    <ChevronsUpDown class="ccn-select__chevron" :size="18" color="#94a3b8" />
                </div>
            </div>

            <!-- Refund section -->
            <div class="ccn-refund">
                <div class="ccn-refund__head">
                    <div class="ccn-refund__head-text">
                        <span class="ccn-refund__title">{{ $t('invoices.credit_note_modal.emit_refund') }}</span>
                        <span class="ccn-refund__subtitle">{{ $t('invoices.credit_note_modal.emit_refund_hint') }}</span>
                    </div>
                    <button
                        type="button"
                        class="ccn-toggle"
                        :class="{ 'ccn-toggle--on': emitRefund }"
                        role="switch"
                        :aria-checked="emitRefund ? 'true' : 'false'"
                        @click="emitRefund = !emitRefund"
                    >
                        <span class="ccn-toggle__knob"></span>
                    </button>
                </div>

                <!-- Total / Partial: no explicit switch for now — total vs partial is auto-detected
                     from the entered sum (sum < full refundable → partial; equal → total). The
                     server re-derives and re-validates it. -->

                <!-- Allocation table (only when emitting a refund) -->
                <div v-if="emitRefund" class="ccn-table">
                    <div class="ccn-table__head">
                        <span class="ccn-col ccn-col--method">{{ $t('invoices.credit_note_modal.payment_method') }}</span>
                        <span class="ccn-col ccn-col--paid">
                            {{
                                hasPriorRefunds
                                    ? $t('invoices.credit_note_modal.refundable')
                                    : $t('invoices.credit_note_modal.paid')
                            }}
                        </span>
                        <span class="ccn-col ccn-col--refund">{{ $t('invoices.credit_note_modal.to_refund') }}</span>
                    </div>

                    <div v-for="row in rows" :key="row.id" class="ccn-table__row">
                        <div class="ccn-col ccn-col--method">
                            <span class="ccn-method-name">{{ row.label }}</span>
                            <span v-if="badgeFor(row)" class="ccn-badge" :class="'ccn-badge--' + badgeFor(row).kind">
                                {{ badgeFor(row).text }}
                            </span>
                        </div>
                        <!-- What REMAINS refundable on this tender (paid − already refunded by prior
                             avoirs), not the gross paid: it is the amount the operator can still
                             route here, and it is what the "to refund" column is pre-filled with. -->
                        <div class="ccn-col ccn-col--paid">
                            <span v-if="rowCap(row) > 0.009">{{ formatCurrency(rowCap(row)) }}</span>
                            <span v-else class="ccn-muted">—</span>
                            <span v-if="row.already_refunded > 0.009" class="ccn-paid-hint">
                                {{
                                    $t('invoices.credit_note_modal.paid_minus_refunded', {
                                        paid: formatCurrency(row.paid),
                                        refunded: formatCurrency(row.already_refunded),
                                    })
                                }}
                            </span>
                        </div>
                        <div class="ccn-col ccn-col--refund">
                            <div class="ccn-amount" :class="{ 'ccn-amount--error': isRowOverCap(row) }">
                                <input
                                    type="number"
                                    class="ccn-amount__input"
                                    min="0"
                                    step="0.01"
                                    :value="row.amount"
                                    @input="onAmountInput(row, $event)"
                                />
                                <span class="ccn-amount__suffix">€</span>
                            </div>
                        </div>
                    </div>

                    <!-- Total row -->
                    <div class="ccn-table__total" :class="{ 'ccn-table__total--error': showBalanceError }">
                        <span class="ccn-col ccn-col--method">{{ $t('invoices.credit_note_modal.total') }}</span>
                        <span class="ccn-col ccn-col--paid">{{ formatCurrency(totalPaid) }}</span>
                        <span class="ccn-col ccn-col--refund ccn-total-refund">{{ formatCurrency(allocatedSum) }}</span>
                    </div>

                    <p v-if="anyRowOverCap" class="ccn-hint ccn-hint--error">
                        {{ $t('invoices.credit_note_modal.cap_error') }}
                    </p>

                    <p v-else-if="showBalanceError" class="ccn-hint ccn-hint--error">
                        {{
                            partialAllowed
                                ? $t('invoices.credit_note_modal.partial_error', {
                                      total: formatCurrency(data.total_refund),
                                  })
                                : $t('invoices.credit_note_modal.balance_error', {
                                      total: formatCurrency(data.total_refund),
                                  })
                        }}
                    </p>
                </div>
            </div>

            <p v-if="error" class="ccn-error">{{ error }}</p>

            <!-- Footer -->
            <div class="ccn-footer">
                <button type="button" class="ccn-btn ccn-btn--ghost" @click="onCancel">
                    {{ $t('invoices.credit_note_modal.cancel') }}
                </button>
                <button type="button" class="ccn-btn ccn-btn--primary" :disabled="!canSubmit" @click="submit">
                    <span v-if="loading" class="ccn-spinner"></span>
                    <span>{{ submitLabel }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { X, ChevronsUpDown } from 'lucide-vue'

/**
 * Credit-note ("facture d'avoir") modal for cancelling an OPEN order.
 *
 * Driven by the HTTP 409 `requires_allocation` payload from POST cancel-order:
 *   { order_id, invoice_number, total_refund, reasons: [key], methods: [{ id, key, label,
 *     paid, already_refunded, default_allocation, execution }] }
 *
 * On submit it POSTs to cancel-order-refund with the operator's Motif, the
 * "Émettre un remboursement" toggle and a per-method allocation. Amounts are routing
 * information only — the refundable total and per-channel caps are re-enforced server-side.
 */
export default {
    name: 'CreateCreditNoteModal',
    components: { X, ChevronsUpDown },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        orderId: {
            type: [Number, String],
            default: null,
        },
        // Specific ValidatedOrderInvoice to scope a DEPOSIT refund to (per-deposit, not
        // per-order). Null for the order-cancel flow (DraftOrders), where it is ignored.
        invoiceId: {
            type: [Number, String],
            default: null,
        },
        // Endpoint that executes the refund/credit-note. Defaults to the order-cancel flow
        // (DraftOrdersDataTable); the validated-invoices list overrides it with the
        // dedicated credit-note endpoint (deposit-in-place vs closed-order routing server-side).
        submitUrl: {
            type: String,
            default: '/back-office/invoices-v3/cancel-order-refund',
        },
        data: {
            type: Object,
            default: () => ({
                order_id: null,
                invoice_number: '',
                total_refund: 0,
                already_credited: 0,
                reasons: [],
                methods: [],
            }),
        },
    },
    data() {
        return {
            motif: '',
            emitRefund: true,
            rows: [],
            loading: false,
            error: null,
        }
    },
    computed: {
        visible: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            },
        },
        allocatedSum() {
            return this.round(this.rows.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0))
        },
        // The invoice/deposit already carries one or more avoirs: `total_refund` is then what
        // REMAINS creditable on it, not the document's full amount.
        hasPriorCreditNotes() {
            return this.round(this.data.already_credited || 0) > 0.009
        },
        // Sum of what REMAINS refundable across the tenders — so the column totals to the same
        // "restant à avoirer" the banner shows, instead of the gross originally collected.
        totalPaid() {
            return this.round(this.rows.reduce((sum, r) => sum + Math.max(0, this.rowCap(r)), 0))
        },
        // At least one tender already carries a refund from a prior avoir: the "Payé" column then
        // reads as "Remboursable" and each row explains paid − already refunded.
        hasPriorRefunds() {
            return this.rows.some((r) => this.round(r.already_refunded) > 0.009)
        },
        allocationBalanced() {
            return Math.abs(this.allocatedSum - this.round(this.data.total_refund)) <= 0.01
        },
        // The Total/Partial switch is offered for the validated-invoices credit-note flows
        // (deposit and closed). The DraftOrders cancel flow (no order_type) keeps the existing
        // total-only behaviour (no switch, amounts editable as before).
        partialAllowed() {
            return this.data.order_type === 'deposit' || this.data.order_type === 'closed'
        },
        // Auto-detected: a sum BELOW the full refundable is a partial refund; equal to it is total.
        // The server re-derives this from refundType and re-validates the amount.
        isPartialMode() {
            return this.partialAllowed && this.allocatedSum < this.round(this.data.total_refund) - 0.01
        },
        // Partial is valid when the sum is strictly positive and does not exceed the full
        // refundable (the server re-checks this and every per-channel cap).
        partialValid() {
            return this.allocatedSum > 0.009 && this.allocatedSum <= this.round(this.data.total_refund) + 0.01
        },
        showBalanceError() {
            // Deposit/closed: any sum in (0, full] is valid (auto total/partial). DraftOrders
            // cancel: the full amount must be allocated exactly.
            return this.partialAllowed ? !this.partialValid : !this.allocationBalanced
        },
        // Auto channels (stripe/fidélité/cure/serie) can never be allocated more than what
        // was paid (minus what is already refunded). The backend rejects it too; we surface
        // it inline so the operator does not submit a doomed allocation.
        anyRowOverCap() {
            return this.rows.some((r) => this.isRowOverCap(r))
        },
        canSubmit() {
            if (this.loading || !this.motif) return false
            if (!this.emitRefund) return true
            if (this.anyRowOverCap) return false
            return this.partialAllowed ? this.partialValid : this.allocationBalanced
        },
        submitLabel() {
            return this.emitRefund
                ? this.$t('invoices.credit_note_modal.submit_refund')
                : this.$t('invoices.credit_note_modal.submit_only')
        },
    },
    watch: {
        value(val) {
            if (val) this.reset()
        },
    },
    created() {
        if (this.value) this.reset()
    },
    methods: {
        reset() {
            this.motif = ''
            this.emitRefund = true
            this.error = null
            this.loading = false
            this.rows = (this.data.methods || []).map((m) => ({
                id: m.id,
                key: m.key,
                label: m.label,
                paid: this.round(m.paid),
                already_refunded: this.round(m.already_refunded),
                execution: m.execution,
                // Pre-filled with the full-refund allocation; the operator edits it down for a
                // partial (sum < full → auto-detected as partial on submit).
                amount: this.round(m.default_allocation),
            }))
        },
        round(n) {
            return Math.round((parseFloat(n) || 0) * 100) / 100
        },
        formatCurrency(amount) {
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        // What a tender can still give back: what was paid on it minus what prior avoirs already
        // refunded on it. Applies to EVERY channel, manual ones included — a tender can never
        // refund more than it took (10 € cash + 10 € CB → 15 € back on the CB is refused). The
        // server enforces the same cap (CancelService::validateAllocations); this only surfaces it.
        rowCap(row) {
            return this.round(row.paid - row.already_refunded)
        },
        isRowOverCap(row) {
            return (parseFloat(row.amount) || 0) > this.rowCap(row) + 0.01
        },
        onAmountInput(row, event) {
            const val = this.round(event.target.value)
            row.amount = val < 0 ? 0 : val
        },
        // Badge shown next to a method, mirroring the screenshot:
        //  - auto           → green "Remboursé automatiquement" (stripe/fidélité/cure/serie and
        //                     bon cadeau — the original voucher balance is restored)
        //  - auto_credited  → green "Crédité automatiquement" (original wallet balance credited)
        //  - manual_operation, NOT paid on this order → gray "Opération manuelle"
        //  - manual_operation already paid (cash/CB back on itself) → no badge (self-evident)
        badgeFor(row) {
            if (row.execution === 'auto_credited') {
                return { kind: 'green', text: this.$t('invoices.credit_note_modal.badge_credited') }
            }
            if (row.execution === 'auto') {
                return { kind: 'green', text: this.$t('invoices.credit_note_modal.badge_auto') }
            }
            if (row.execution === 'manual_operation' && row.paid <= 0) {
                return { kind: 'gray', text: this.$t('invoices.credit_note_modal.badge_manual') }
            }
            return null
        },
        onCancel() {
            this.visible = false
            this.$emit('cancel')
        },
        async submit() {
            if (!this.canSubmit) return
            this.loading = true
            this.error = null

            const payload = {
                orderId: this.orderId,
                // Sent only by the validated-invoices flow; scopes a DEPOSIT refund to one invoice.
                ...(this.invoiceId != null ? { invoiceId: this.invoiceId } : {}),
                motif: this.motif,
                emitRefund: this.emitRefund,
                refundType: this.isPartialMode ? 'partial' : 'total',
                allocations: this.emitRefund
                    ? this.rows
                          .filter((r) => (parseFloat(r.amount) || 0) > 0)
                          .map((r) => ({ payment_method_id: r.id, amount: this.round(r.amount) }))
                    : [],
            }

            try {
                const response = await this.axios.post(this.submitUrl, payload)
                if (response.data && response.data.success) {
                    this.visible = false
                    this.$emit('done', response.data.data)
                } else {
                    this.error = (response.data && response.data.message) || this.$t('invoices.credit_note_modal.error_occured')
                }
            } catch (error) {
                const res = error.response
                // 422 carries the server-side allocation/motif validation message.
                this.error =
                    res?.data?.errors?.allocations?.[0] ||
                    res?.data?.message ||
                    this.$t('invoices.credit_note_modal.error_occured')
            } finally {
                this.loading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.ccn-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background-color: rgba(2, 6, 23, 0.85);
    overflow-y: auto;
}

.ccn-modal {
    position: relative;
    width: 100%;
    max-width: 620px;
    margin: auto;
    padding: 28px;
    background-color: #1a1438;
    border: 1px solid #3b2f6b;
    border-radius: 22px;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.55);
    color: #f1f5f9;
    font-family: inherit;
}

/* Header */
.ccn-header {
    position: relative;
    display: flex;
    align-items: center;
    margin-bottom: 22px;
}

.ccn-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
}

.ccn-close {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border: none;
    border-radius: 9999px;
    background-color: #2b2356;
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #3a3070;
    }
}

/* Gradient banner */
.ccn-banner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 22px;
    border-radius: 14px;
    background: linear-gradient(90deg, #7c3aed 0%, #4f46e5 100%);
    color: #ffffff;

    &__col {
        display: flex;
        flex-direction: column;

        &--right {
            align-items: flex-end;
            text-align: right;
        }
    }

    &__label {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.8);
        margin-bottom: 4px;
    }

    &__value {
        font-size: 20px;
        font-weight: 700;
    }

    &__hint {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.8);
        margin-top: 4px;
    }
}

/* Fields */
.ccn-field {
    margin-bottom: 20px;
}

.ccn-label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #cbd5e1;
}

.ccn-required {
    color: #c084fc;
}

.ccn-select-wrapper {
    position: relative;
}

.ccn-select {
    width: 100%;
    padding: 14px 44px 14px 16px;
    background-color: #221a47;
    border: 1px solid #3b2f6b;
    border-radius: 12px;
    color: #f1f5f9;
    font-size: 14px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;

    &:focus {
        outline: none;
        border-color: #8b5cf6;
    }

    option {
        color: #f1f5f9;
        background-color: #221a47;
    }

    &__chevron {
        position: absolute;
        top: 50%;
        right: 16px;
        transform: translateY(-50%);
        pointer-events: none;
    }
}

/* Refund section */
.ccn-refund {
    padding: 18px;
    border: 1px solid #3b2f6b;
    border-radius: 16px;
    margin-bottom: 22px;

    &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }

    &__head-text {
        display: flex;
        flex-direction: column;
    }

    &__title {
        font-size: 15px;
        font-weight: 600;
        color: #ffffff;
    }

    &__subtitle {
        font-size: 12px;
        color: #94a3b8;
        margin-top: 2px;
    }
}

/* Toggle */
.ccn-toggle {
    position: relative;
    flex-shrink: 0;
    width: 48px;
    height: 26px;
    border: none;
    border-radius: 9999px;
    background-color: #3b2f6b;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &--on {
        background: linear-gradient(90deg, #a855f7 0%, #7c3aed 100%);
    }

    &__knob {
        position: absolute;
        top: 3px;
        left: 3px;
        width: 20px;
        height: 20px;
        border-radius: 9999px;
        background-color: #ffffff;
        transition: transform 0.2s ease;
    }

    &--on &__knob {
        transform: translateX(22px);
    }
}

/* Allocation table */
.ccn-table {
    margin-top: 18px;

    &__head {
        display: flex;
        align-items: center;
        padding: 0 0 10px;
        border-bottom: 1px solid #2b2356;
        font-size: 12px;
        color: #94a3b8;
    }

    &__row {
        display: flex;
        align-items: center;
        padding: 14px 0;
        border-bottom: 1px solid #2b2356;
    }

    &__total {
        display: flex;
        align-items: center;
        padding: 14px 0 2px;
        font-weight: 700;
        color: #ffffff;

        .ccn-total-refund {
            color: #c084fc;
        }

        &--error .ccn-total-refund {
            color: #f87171;
        }
    }
}

.ccn-col {
    &--method {
        flex: 1 1 auto;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    &--paid {
        flex: 0 0 130px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        text-align: left;
        font-size: 14px;
        color: #e2e8f0;
    }

    &--refund {
        flex: 0 0 130px;
        display: flex;
        justify-content: flex-end;
    }
}

.ccn-method-name {
    font-size: 14px;
    color: #f1f5f9;
}

.ccn-muted {
    color: #64748b;
}

.ccn-paid-hint {
    font-size: 11px;
    line-height: 1.3;
    color: #64748b;
    margin-top: 2px;
}

/* Badges */
.ccn-badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.4;

    &--green {
        background-color: rgba(34, 197, 94, 0.15);
        color: #4ade80;
    }

    &--gray {
        background-color: rgba(148, 163, 184, 0.15);
        color: #cbd5e1;
    }
}

/* Amount input */
.ccn-amount {
    position: relative;
    width: 110px;

    &__input {
        width: 100%;
        padding: 10px 28px 10px 12px;
        background-color: #221a47;
        border: 1px solid #3b2f6b;
        border-radius: 10px;
        color: #f1f5f9;
        font-size: 14px;
        text-align: left;
        -moz-appearance: textfield;

        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        &:focus {
            outline: none;
            border-color: #8b5cf6;
        }
    }

    &__suffix {
        position: absolute;
        top: 50%;
        right: 12px;
        transform: translateY(-50%);
        font-size: 13px;
        color: #94a3b8;
        pointer-events: none;
    }

    &--error &__input {
        border-color: #ef4444;
    }
}

.ccn-hint {
    margin: 10px 0 0;
    font-size: 12px;

    &--error {
        color: #f87171;
    }
}

.ccn-error {
    margin: -6px 0 16px;
    font-size: 12px;
    color: #f87171;
}

/* Footer */
.ccn-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
}

.ccn-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
    padding: 0 24px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition:
        opacity 0.15s ease,
        background-color 0.15s ease;

    &--ghost {
        background-color: transparent;
        border: 1px solid #3b2f6b;
        color: #ffffff;

        &:hover {
            background-color: rgba(255, 255, 255, 0.05);
        }
    }

    &--primary {
        border: none;
        color: #ffffff;
        background: linear-gradient(90deg, #7c3aed 0%, #4f46e5 100%);

        &:hover:not(:disabled) {
            opacity: 0.92;
        }

        &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
        }
    }
}

.ccn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 9999px;
    animation: ccn-spin 0.6s linear infinite;
}

@keyframes ccn-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive */
@media (max-width: 520px) {
    .ccn-modal {
        padding: 20px;
    }

    .ccn-banner {
        flex-direction: column;
        align-items: flex-start;

        &__col--right {
            align-items: flex-start;
            text-align: left;
        }
    }

    .ccn-col--paid {
        flex-basis: 80px;
    }

    .ccn-col--refund {
        flex-basis: 110px;
    }

    .ccn-footer {
        flex-direction: column-reverse;
        align-items: stretch;
    }
}
</style>
